/*
 * File: TodaysMeetingCard.tsx
 * Project: a-a
 * File Created: Tuesday, 19th November 2024 9:04:33 pm
 * Author: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Last Modified: Monday, 15th September 2025 11:08:24 pm
 * Modified By: Jackson Thounaojam (jackson.thounaojam@mutualmobile.com)
 * -----
 * Copyright 2020 - 2024 Mutual Mobile, Mutual Mobile
 */
import React, { useState } from 'react';
import CustomCard from '../../../CustomCard/CustomCard';
import zeplinColor from '../../../../../theme/Color';
import { Box, Skeleton, Typography, useTheme } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Utilities } from '../../../../../utilities/utilities';
import CustomPopOver from '../../../CustomPopOver/CustomPopOver';
import { LightTooltip } from '../../../CustomTooltip/CustomTooltip';
import styles from './TodaysMeetingCard.module.css';
type TodaysMeeting = {
  attendees: string[];
  body: string;
  end: string;
  location: string;
  meetingLink: string;
  organizer: string;
  start: string;
  subject: string;
};
const TodaysMeetingCard = ({
  data,
  loading = false,
}: {
  data: TodaysMeeting;
  loading: boolean;
}) => {
  const theme = useTheme();
  const start = Utilities.formatTime(new Date(data?.start));
  const end = Utilities.formatTime(new Date(data?.end));
  const [anchorEl, setAnchorEl] = React.useState<HTMLImageElement | null>(null);
  const [copy, setCopy] = useState('');
  const handleClick = (event: React.MouseEvent<HTMLImageElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCopyLink = (val: any) => {
    navigator.clipboard.writeText(val);
    setCopy('Link Copied');
  };
  const handleClose = () => {
    setCopy('');
  };
  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;
  return (
    <CustomCard
      className={styles['todays-meeting-card']}
      styles={{
        // borderRadius: '14px',

        backgroundColor: zeplinColor.Neutral1,
        borderWidth: `0px 0px 1px 0px`,
        borderStyle: 'solid',
        borderColor: `${zeplinColor.Neutral5}`,
        display: 'flex',
        // flexDirection: 'column',

        // justifyContent: 'space-between',
      }}
    >
      {loading ? (
        <Skeleton variant='rounded' width={32} height={32} />
      ) : (
        <img
          src='/assets/microsoft-team.svg'
          className={styles['meeting-icon']}
          style={{ objectFit: 'fill' }}
        />
      )}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          //   flexDirection: 'column',
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flex: 1,
          }}
        >
          {loading ? (
            <Skeleton
              variant='text'
              sx={{
                fontSize: theme.typography.body2,
                width: '100px',
                // Optionally adjust other styles here
              }}
            />
          ) : (
            <Typography variant='responsiveBody2' style={{ textAlign: 'left' }}>
              {data?.subject}
            </Typography>
          )}
          <Box
            sx={{
              display: 'flex',
              // justifyContent: 'space-between',
              //   flexDirection: 'column',
              gap: '8px',
              flex: 1,
              alignItems: 'center',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100px',
                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
                {start}-{end}
              </Typography>
            )}

            <Box
              sx={{
                height: '16px',
                width: '1px',
                backgroundColor: zeplinColor.Neutral5,
                display: 'inline-block',
                marginX: '4px', // Add some margin on both sides
              }}
            />
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  width: '100px',
                  // Optionally adjust other styles here
                }}
              />
            ) : (
              <Typography variant='responsiveLabel1' color={zeplinColor.Neutral8}>
                Microsoft Teams
              </Typography>
            )}
          </Box>
        </Box>
        {/* <MoreVertIcon /> */}
        <Box
          sx={{
            position: 'relative',
            width: '24px',
            height: '24px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          {/* <Button aria-describedby={id} onClick={handleClick}> */}
          <LightTooltip
            title={
              <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                Options
              </Typography>
            }
            placement='bottom-end'
            style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            // sx={{ [`& .${tooltipClasses.tooltip}`]: { backgroundColor: zeplinColor.Neutral1 } }}
          >
            {/* <MoreVertIcon onClick={handleClick} aria-hidden='false' /> */}
            <img
              className={styles['options-icon']}
              src={'/assets/vertical-dots.svg'}
              alt='options-icon'
              onClick={handleClick}
              aria-hidden='false'
              // style={{ imageRendering: 'pixelated', filter: 'blur(0px)' }}
            />
          </LightTooltip>
          {/* </Button> */}
          <CustomPopOver
            setAnchorEl={setAnchorEl}
            anchorEl={anchorEl}
            id={id}
            sx={{ borderRadius: '14px', boxShadow: 'rgba(0, 0, 0, 0.15)' }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', padding: '16px' }}>
              <Box
                sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}
                onClick={() => handleCopyLink(data?.meetingLink)}
              >
                <img src={'/assets/copy-link.svg'} className={styles['icon']} />
                <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                  Copy Link
                </Typography>
              </Box>
              <a
                href={data?.meetingLink}
                target='_blank'
                rel='noopener noreferrer'
                style={{ textDecoration: 'none' }}
              >
                <Box sx={{ display: 'flex', gap: '9px', cursor: 'pointer' }}>
                  <img src={'/assets/redirect-icon.svg'} className={styles['icon']} />
                  <Typography variant='responsiveBody3' color={zeplinColor.BasicBlack}>
                    Join meeting
                  </Typography>
                </Box>
              </a>
            </Box>
          </CustomPopOver>
        </Box>
      </Box>
    </CustomCard>
  );
};

export default TodaysMeetingCard;
